import React, { useEffect, useMemo } from "react";

import { EntityType, RelatedAs } from "../../../api/stakeholder_relationships";
import { useAppSelector } from "../../../app/store";
import { AutoSelectVerticalNav } from "../../../components/ui/navigation/AutoSelectVerticalNav";
import { TabItem } from "../../../components/ui/navigation/VerticalNavPanel";
import { getCreditAccounts } from "../../creditAccounts/logic/slice";
import { getUniqueStakeholdersByEntity } from "../../stakeholder/logic/stakeholderSlice";

interface LoanAccountStakeholderNavProps {
  selectedLoanReferenceId?: string;
  selectedStakeholderId?: string;
  onSelectionChange: (loanReferenceId: string, stakeholderId: string) => void;
}

const NAV_STAKEHOLDER_ROLES: RelatedAs[] = [
  RelatedAs.PRIMARY_BORROWER,
  RelatedAs.CO_BORROWER,
  RelatedAs.GUARANTOR,
  RelatedAs.PROPRIETOR,
  RelatedAs.PARTNER,
  RelatedAs.DIRECTOR,
  RelatedAs.PROMOTER,
  RelatedAs.TRUSTEE,
  RelatedAs.BORROWER, // legacy, until deprecated
];

const buildNavId = (loanReferenceId: string, stakeholderId: string) =>
  `${loanReferenceId}::${stakeholderId}`;

const LoanAccountStakeholderNav: React.FC<LoanAccountStakeholderNavProps> = ({
  selectedLoanReferenceId,
  selectedStakeholderId,
  onSelectionChange,
}) => {
  const creditAccounts = useAppSelector(getCreditAccounts);

  const stakeholdersByAccount = useAppSelector((state) =>
    creditAccounts.map((account) => ({
      account,
      stakeholders: getUniqueStakeholdersByEntity(
        state,
        EntityType.LOAN_ACCOUNT,
        account.account_number,
        NAV_STAKEHOLDER_ROLES,
      ),
    })),
  );

  const items: TabItem[] = useMemo(
    () =>
      stakeholdersByAccount.map(({ account, stakeholders }) => ({
        id: account.account_number,
        header: account.account_name,
        subheader: account.ext_account_number,
        children: stakeholders.map((stakeholder) => ({
          id: buildNavId(account.account_number, stakeholder.stakeholder_id),
          header: stakeholder.name,
        })),
      })),
    [stakeholdersByAccount],
  );

  const selectedId =
    selectedLoanReferenceId && selectedStakeholderId
      ? buildNavId(selectedLoanReferenceId, selectedStakeholderId)
      : undefined;

  useEffect(() => {
    if (selectedId) return;

    const firstAccountWithPeople = items.find(
      (item) => (item.children?.length ?? 0) > 0,
    );
    const firstStakeholder = firstAccountWithPeople?.children?.[0];
    if (!firstStakeholder) return;

    const [loanReferenceId, stakeholderId] = firstStakeholder.id.split("::");
    onSelectionChange(loanReferenceId, stakeholderId);
  }, [items, selectedId, onSelectionChange]);

  const handleSelectionChange = (id: string) => {
    const [loanReferenceId, stakeholderId] = id.split("::");
    if (!stakeholderId) return;

    onSelectionChange(loanReferenceId, stakeholderId);
  };

  return (
    <AutoSelectVerticalNav
      title="Loan Accounts"
      items={items}
      selectedId={selectedId}
      onSelectionChange={handleSelectionChange}
      className="w-[260px] shrink-0"
    />
  );
};

export default LoanAccountStakeholderNav;
